import React, { useCallback, useState, useRef, useMemo, useEffect, useContext } from "react";
import MSection from "components/Shared/MSection";
import { QuickFilterContext, QuickFilterDefinition, QuickFilterProviderValue } from "./QuickFilterProvider";
import { Button, MSelect } from "components/Shared";
import ValueEditor from "components/Homeowner/Dashboard/QueryBuilder/controls/ValueEditor";
import { QuickListFilterField } from "interfaces/Mueller.Business.Models";
import { operators } from "components/Homeowner/Dashboard/QuickListConfig";
import IconButton from "components/Shared/IconButton";

interface QuickFilterRowProps {
  filter: QuickFilterDefinition;
}

const QuickFilterRow = ({ filter }: QuickFilterRowProps) => {
  const { changeFilter, filterDefs, removeFilter, filters } = useContext<QuickFilterProviderValue>(QuickFilterContext);

  const allFilterOptions = filterDefs.map(f => ({ label: f.name, value: f.key }));
  const limitedFilterOptions = allFilterOptions.filter(fd => !filters.find(f=> f.filterKey === fd.value));

  const filterDef = filterDefs.find(fd => fd.key === filter.filterKey);
  
  const operatorOptions = filterDef.operators.map(fo => {
    let op = operators.find(o => o.name === fo);
    return ({ label: op.label, value: op.name });
  });

  // You may need to supply additional props from Redux for ValueEditor,
  // but since ValueEditor is already connected, its own mapStateToProps should supply those.
  return (
    <div className="quick-filter-row">
      <MSelect
        className="filter-type"
        options={limitedFilterOptions}
        onChange={selected => changeFilter({ clientId: filter.clientId, filterKey: selected.value })}
        value={allFilterOptions.find(o => o.value === filter.filterKey)}
        isClearable={false}
      />
      <MSelect
        className="filter-operator"
        options={operatorOptions}
        onChange={selected => changeFilter({ clientId: filter.clientId, operator: selected.value })}
        value={operatorOptions.find(o => o.value === filter.operator)}
        isClearable={false}
      />
      <ValueEditor
        className="filter-value"
        field={filterDef.key}
        operator={filter.operator}
        value={filter.value}
        inputType={filterDef.inputType}
        handleOnChange={value => changeFilter({ clientId: filter.clientId, value })}
        title={filterDef.name}
        simpleMode={false}
        disableMSelectPortal
      />
      <IconButton icon="delete" onClick={() => removeFilter(filter.clientId)} />
    </div>
  );
};

const QuickFilterSection = () => {
  const {
    applyFilters,
    clearFilters,
    addFilter,
    filters,
    initQuickFilters,
    hasUnappliedChanges,
  } = useContext<QuickFilterProviderValue>(QuickFilterContext);

  useEffect(() => {
    initQuickFilters();
  }, []);
  
  const onAdd = () => {
    addFilter();
  };

  return (
    <MSection title="Quick Filters" className="quick-filters-section">
      <div className="button-toolbar">
        <Button onClick={onAdd}>Add Filter</Button>
        <div className="spacer">{hasUnappliedChanges && "Unapplied changes present"}</div>
        <Button onClick={clearFilters}>Clear</Button>
        <Button onClick={applyFilters}>Apply</Button>
      </div>
      <div className="filter-list">
        {filters.map(f => <QuickFilterRow filter={f} />)}
      </div>
    </MSection>
  );
};

export default QuickFilterSection;
